import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import AudioPlayer from './AudioPlayer';
import { TEST_IDS } from '@/constants/testIds';

const NAV_LINKS = [
  { label: 'Method', href: '#method', testId: TEST_IDS.navMethodLink },
  { label: 'Credentials', href: '#authority', testId: TEST_IDS.navCredentialsLink },
  { label: 'Testimonials', href: '#testimonials', testId: TEST_IDS.navTestimonialsLink },
  { label: 'Process', href: '#process', testId: TEST_IDS.navProcessLink },
  { label: 'FAQ', href: '#faq', testId: TEST_IDS.navFaqLink },
];

const SECTION_IDS = ['hero', 'agitation', 'method', 'authority', 'testimonials', 'process', 'faq', 'cta'];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const updateActiveSection = useCallback(() => {
    const scrollY = window.scrollY + 200;
    for (let i = SECTION_IDS.length - 1; i >= 0; i--) {
      const el = document.getElementById(SECTION_IDS[i]);
      if (el && el.offsetTop <= scrollY) {
        setActiveSection(SECTION_IDS[i]);
        break;
      }
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      updateActiveSection();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [updateActiveSection]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const isActive = (href) => {
    const sectionId = href.replace('#', '');
    return activeSection === sectionId;
  };

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#F7F5F2]/95 backdrop-blur-xl border-b border-[#1B2A4A]/10 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a
          href="#hero"
          data-testid={TEST_IDS.navLogo}
          onClick={(e) => handleNavClick(e, '#hero')}
          className="font-serif text-lg md:text-xl text-[#1B2A4A] tracking-tight hover:text-[#C9A96E] transition-colors duration-300"
        >
          <motion.span whileHover={{ letterSpacing: '0.02em' }} transition={{ duration: 0.3 }}>
            Sarah Watson
          </motion.span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`hover-line text-xs uppercase tracking-[0.2em] font-sans font-medium transition-colors duration-300 ${
                isActive(link.href) ? 'text-[#C9A96E] nav-active' : 'text-[#2D2D2D] hover:text-[#1B2A4A]'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Right side — single AudioPlayer + conditional CTA/hamburger */}
        <div className="flex items-center gap-2 lg:gap-4">
          <AudioPlayer />
          <motion.a
            href="#cta"
            data-testid={TEST_IDS.navCtaBtn}
            onClick={(e) => handleNavClick(e, '#cta')}
            className="hidden lg:inline-flex bg-[#D4853A] text-white px-6 py-2.5 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#1B2A4A] transition-colors duration-300 items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Book a Conversation
            <ArrowRight className="w-3.5 h-3.5" />
          </motion.a>
          <motion.button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid={TEST_IDS.mobileMenuBtn}
            className="p-2 text-[#1B2A4A] lg:hidden"
            whileTap={{ scale: 0.9 }}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid={TEST_IDS.mobileMenu}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-[#F7F5F2]/98 backdrop-blur-xl border-b border-[#1B2A4A]/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-${link.testId}`}
                  onClick={(e) => handleNavClick(e, link.href)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`text-sm uppercase tracking-[0.15em] font-sans font-medium py-2 transition-colors ${
                    isActive(link.href) ? 'text-[#C9A96E]' : 'text-[#2D2D2D]'
                  }`}
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="#cta"
                onClick={(e) => handleNavClick(e, '#cta')}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[#D4853A] text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-sans font-medium text-center mt-2 inline-flex items-center justify-center gap-2"
              >
                Book a Conversation
                <ArrowRight className="w-3.5 h-3.5" />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
